import { Injectable } from '@angular/core';
import { EcommerceTrackingMapperService } from '@epicuro-next/addons/ecommerce-tracking-mappers';
import {
  PulseConfigurationKeys,
  PulseConfigurations,
  PULSE_FEATURE_NAME,
} from '@epicuro-next/constants/rule-engine/pulse-rules';
import { IUser } from '@epicuro-next/data/users';
import { ConfigurationsService } from '@epicuro-next/platform/configuration';
import { EcommerceTrackingEvents } from '@epicuro-next/platform/tracking';
import { selectActiveAccount } from '@epicuro-next/state/accounts-state';
import { gtmTrackActions } from '@epicuro-next/state/tracking';
import { selectUser } from '@epicuro-next/state/user-state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { Account, BrandOrg } from 'graphql-api/gql-api-interfaces';
import { forkJoin } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

import {
  processNewItemsPrepack,
  processNewItemsSize,
} from '../../shared/map-gtm-basket-lines';
import {
  checkBasketSuccess,
  deleteBasket,
  loadDtoBasketSuccess,
} from '../actions';
import {
  selectExtendedBasketItems,
  selectIsBasketStatusValid,
  selectReferencedProducts,
} from '../selectors';

@Injectable()
export class UpdateBasketEcommerceTrackingEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private ecommerceTrackingMapper: EcommerceTrackingMapperService,
    private configService: ConfigurationsService,
  ) {}

  private trackCheckBasketOnFailure$ = this.configService.getConfigValue<
    PulseConfigurations,
    PulseConfigurationKeys.GTMEcommerceTrackCheckBasketOnFailure
  >({
    featureName: PulseConfigurationKeys.GTMEcommerceTrackCheckBasketOnFailure,
    namespace: PULSE_FEATURE_NAME,
    defaultValue: false,
  });

  public checkBasketSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(checkBasketSuccess),
      switchMap(() => {
        return this.actions$.pipe(ofType(loadDtoBasketSuccess), take(1));
      }),
      concatLatestFrom(() => [
        this.store.pipe(select(selectIsBasketStatusValid)),
        this.store.pipe(select(selectExtendedBasketItems)),
        this.trackCheckBasketOnFailure$,
        this.store.pipe(select(selectReferencedProducts)),
        this.store.select(selectActiveAccount),
        this.store.select(selectUser),
      ]),
      filter(([, statusValid, , trackCheckBasketOnFailure]) =>
        !trackCheckBasketOnFailure ? statusValid : true,
      ),
      switchMap(
        ([, statusValid, items, , referencedProducts, activeAccount, user]) => {
          const extendedItems = new Map();

          processNewItemsSize(items, [], extendedItems);
          processNewItemsPrepack(items, [], referencedProducts, extendedItems);

          return forkJoin([
            this.ecommerceTrackingMapper.mapBasketProducts([
              ...extendedItems.values(),
            ]),
            this.ecommerceTrackingMapper.mapCheckBasketActionField({
              statusValid,
            }),
          ]).pipe(
            map(([products, actionField]) => {
              return gtmTrackActions.gtmEcommerceTrackEvent({
                value: {
                  event: EcommerceTrackingEvents.Checkout,
                  ecommerce: {
                    checkout: {
                      actionField,
                      products,
                    },
                  },
                  user: {
                    userId: (user as IUser)?.email,
                    account: (activeAccount as Account)?.externalId,
                    user_role: (user as IUser)?.roles?.join(', '),
                    brand_org_id: (activeAccount as Account)?.brandOrganisations
                      ?.length
                      ? (
                          (activeAccount as Account)
                            .brandOrganisations as BrandOrg[]
                        )[0].brandOrganisationId
                      : undefined,
                  },
                  clearPrevious: true,
                },
              });
            }),
          );
        },
      ),
    ),
  );

  public deleteBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteBasket),
      map(({ basketId }) => {
        return gtmTrackActions.gtmEcommerceTrackEvent({
          value: {
            event: EcommerceTrackingEvents.DeleteBasket,
            basket_id: basketId,
            clearPrevious: true,
          },
        });
      }),
    ),
  );
}
